import useNaicsSectors from "../../../shared/hooks/useNaicsSectors";
import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ContactsIcon from "@mui/icons-material/Contacts";
import BusinessIcon from "@mui/icons-material/Business";
import FacebookIcon from "@mui/icons-material/Facebook";
import LanguageIcon from "@mui/icons-material/Language";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import TwitterIcon from "@mui/icons-material/Twitter";
import CloudDoneIcon from "@mui/icons-material/CloudDone";
import SearchIcon from "@mui/icons-material/Search";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";

import {
  TABLE_STYLES,
  TABLE_EXPLORE_MENU_ITEM_SX,
  TABLE_ICON_BLUE_SX,
  TABLE_ICON_GREEN_SX,
  TABLE_ROW_HOVER_SX,
} from "../../../theme/tablestyles";
import { SELECT_MENU_PROPS } from "../../../theme/filterstyles";
import { US_STATE_MAP } from "../../../utils/States";
import {
  formatBRELocationLabel,
  formatBroadSectorLabel,
  normalizeCityLabel,
  normalizeIndustryLabel,
} from "./BRECompanyHelpers";
import { getSectorDisplayParts } from "../../../constants/sectors";
const getWebsiteUrl = (website) =>
  website?.startsWith("http") ? website : `https://${website}`;
const getBreNaicsLabel = (company) =>
  company?.industry_custom || company?.naics_text || company?.industry || "-";
const EXPLORE_COLUMN_WIDTH = 88;
const NAME_COLUMN_WIDTH = 300;
const LOCATION_COLUMN_WIDTH = 220;
const SECTOR_COLUMN_WIDTH = 190;
const NAICS_COLUMN_WIDTH = 260;
const NAICS_EXPLORE_GUTTER = 28;

const STATUS_CONFIG = {
  "Needs Outreach":      { bg: "#FFF3E0", color: "#B45309", border: "#FCD34D" },
  "Engaged":             { bg: "#DBEAFE", color: "#1D4ED8", border: "#93C5FD" },
  "Support In Progress": { bg: "#EDE9FE", color: "#6D28D9", border: "#C4B5FD" },
  "At Risk":             { bg: "#FEE2E2", color: "#B91C1C", border: "#FCA5A5" },
  "Out of Business":     { bg: "#F1F5F9", color: "#64748B", border: "#CBD5E1" },
};
const NO_STATUS_CONFIG = { bg: "#F3F4F6", color: "#9CA3AF", border: "#E5E7EB" };
const STATUS_TOOLTIPS = {
  "Needs Outreach": "The company needs initial outreach or a fresh follow-up.",
  "Engaged": "The company is actively engaged with the team.",
  "Support In Progress": "The team is working on an active support request or retention need.",
  "At Risk": "The company may need intervention due to a retention, expansion, or operating concern.",
  "Out of Business": "The company is no longer operating.",
  "No Status": "No BRE pipeline status has been assigned yet.",
};
const DEFAULT_STATUS_TOOLTIP = "Current BRE company pipeline status.";

const HEAD_CELL_SX = {
  backgroundColor: "#00336C",
  color: "#ffffff",
  fontSize: "0.72rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  whiteSpace: "nowrap",
  borderBottom: "none",
  py: 1.5,
};

const HEADER_TRIGGER_SX = {
  minWidth: 108,
  minHeight: 28,
  px: 1.5,
  py: 0.35,
  fontWeight: 700,
  fontSize: "0.72rem",
  color: "#00336C",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  borderRadius: 1.5,
  border: "1.5px solid rgba(255,255,255,0.35)",
  backgroundColor: "rgba(255,255,255,0.92)",
  "&:hover": { backgroundColor: "#ffffff", borderColor: "rgba(255,255,255,0.8)" },
};

const SEARCH_FIELD_SX = {
  px: 1,
  pt: 0.75,
  pb: 0.25,
  backgroundColor: "white",
  position: "sticky",
  top: 0,
  zIndex: 1,
};

const FILTER_MENU_WIDTH = 360;
const FILTER_MENU_MAX_WIDTH = "calc(100vw - 32px)";

const FILTER_SEARCH_INPUT_SX = {
  "& .MuiInputBase-root": {
    fontSize: "0.875rem",
    backgroundColor: "#ffffff",
  },
  "& .MuiInputBase-input": {
    py: 0.75,
    color: "#00336C",
    fontWeight: 700,
  },
  "& input": {
    color: "#00336C !important",
    WebkitTextFillColor: "#00336C",
    caretColor: "#00336C",
  },
  "& .MuiOutlinedInput-root": {
    "& fieldset": { border: "none" },
    "&:hover fieldset": { border: "none" },
    "&.Mui-focused fieldset": { border: "none" },
  },
};



const isFilterActive = (value, filterKey, filters) => {
  if (filterKey === "name") return Boolean(filters?.pipeline_stage);
  return Array.isArray(value) ? value.length > 0 : Boolean(value);
};

function BRECompaniesTable({
  companies,
  columns,
  orderBy,
  orderDirection,
  onSort,
  selectedIds,
  allSelected,
  someSelected,
  toggleOne,
  togglePage,
  navigate,
  detailRouteBase = "/bre-companies",
  handleOpenProfile,
  handleUpdateStage,
  filters,
  onFilterChange,
  uniqueValues,
  PIPELINE_STAGE_OPTIONS,
}) {
  const [activeFilter, setActiveFilter] = useState(null);
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [filterSearch, setFilterSearch] = useState("");
  const [exploreAnchorEl, setExploreAnchorEl] = useState(null);
  const [activeExploreCompany, setActiveExploreCompany] = useState(null);
  const [statusChangeAnchor, setStatusChangeAnchor] = useState(null);
  const [statusChangeCompany, setStatusChangeCompany] = useState(null);

  const { options: sectorOptions } = useNaicsSectors(uniqueValues("sector"));
  const industryOptions = useMemo(
    () => uniqueValues("industry").map(normalizeIndustryLabel).filter(Boolean),
    [uniqueValues],
  );
  const cityOptions = useMemo(
    () =>
      Array.from(
        new Map(
          companies
            .map((company) => {
              const city = normalizeCityLabel(company?.city);
              const state = String(company?.state || "").trim();
              const label = formatBRELocationLabel({ city, state });

              if (!city || label === "-") {
                return null;
              }

              return [label, { label, city }];
            })
            .filter(Boolean),
        ).values(),
      ).sort((a, b) => a.label.localeCompare(b.label)),
    [companies],
  );
  const statusOptions = useMemo(
    () => ["No Status", ...PIPELINE_STAGE_OPTIONS],
    [PIPELINE_STAGE_OPTIONS],
  );
  const isExploreMenuOpen = Boolean(exploreAnchorEl);

  const lowerSearch = filterSearch.trim().toLowerCase();
  const filteredCityOptions = cityOptions.filter((option) =>
    option.label.toLowerCase().includes(lowerSearch),
  );
  const filteredSectorOptions = sectorOptions.filter((option) =>
    formatBroadSectorLabel(option).toLowerCase().includes(lowerSearch),
  );
  const filteredIndustryOptions = industryOptions.filter((option) =>
    option.toLowerCase().includes(lowerSearch),
  );
  const filteredStatusOptions = statusOptions.filter((option) =>
    option.toLowerCase().includes(lowerSearch),
  );

  const exploreActions = useMemo(() => {
    if (!activeExploreCompany) return [];

    const linkActions = [
      {
        key: "website",
        label: "Website",
        icon: <LanguageIcon sx={{ ...TABLE_ICON_BLUE_SX, fontSize: 22 }} />,
        href: activeExploreCompany.website,
      },
      {
        key: "facebook",
        label: "Facebook",
        icon: <FacebookIcon sx={{ ...TABLE_ICON_BLUE_SX, fontSize: 22 }} />,
        href: activeExploreCompany.facebook,
      },
      {
        key: "linkedin",
        label: "LinkedIn",
        icon: <LinkedInIcon sx={{ ...TABLE_ICON_BLUE_SX, fontSize: 22 }} />,
        href: activeExploreCompany.linkedin,
      },
      {
        key: "twitter",
        label: "Twitter / X",
        icon: <TwitterIcon sx={{ ...TABLE_ICON_BLUE_SX, fontSize: 22 }} />,
        href: activeExploreCompany.twitter,
      },
    ]
      .filter((action) => action.href)
      .map((action) => ({
        ...action,
        onClick: () =>
          window.open(getWebsiteUrl(action.href), "_blank", "noopener,noreferrer"),
      }));

    return [
      {
        key: "profile",
        label: "Company Profile",
        icon: <BusinessIcon sx={{ ...TABLE_ICON_GREEN_SX, fontSize: 22 }} />,
        onClick: () => handleOpenProfile(activeExploreCompany),
      },
      {
        key: "contacts",
        label: "Contacts",
        icon: <ContactsIcon sx={{ ...TABLE_ICON_BLUE_SX, fontSize: 22 }} />,
        onClick: () =>
          navigate(`/bre-contacts?company_id=${activeExploreCompany.id}`),
      },
      ...linkActions,
    ];
  }, [activeExploreCompany, handleOpenProfile, navigate]);

  const openFilterMenu = useCallback((event, filterKey) => {
    setActiveFilter(filterKey);
    setFilterAnchor(event.currentTarget);
    setFilterSearch("");
  }, []);

  const closeFilterMenu = useCallback(() => {
    setActiveFilter(null);
    setFilterAnchor(null);
    setFilterSearch("");
  }, []);

  const handleOpenExploreMenu = useCallback((event, company) => {
    setExploreAnchorEl(event.currentTarget);
    setActiveExploreCompany(company);
  }, []);

  const handleCloseExploreMenu = useCallback(() => {
    setExploreAnchorEl(null);
    setActiveExploreCompany(null);
  }, []);

  const clearActiveFilter = useCallback(() => {
    if (activeFilter === "sector") {
      onFilterChange("sector", []);
    } else if (activeFilter === "industry") {
      onFilterChange("industry", []);
    } else if (activeFilter === "name") {
      onFilterChange("pipeline_stage", "");
    } else if (activeFilter) {
      onFilterChange(activeFilter, "");
    }
  }, [activeFilter, onFilterChange]);

  const renderFilterTrigger = (
    label,
    filterKey,
    sortable = false,
    sortKey = filterKey,
  ) => {
    const active = isFilterActive(filters[filterKey], filterKey, filters);
    const minWidthByFilterKey = {
      city: 124,
      sector: 132,
      industry: 160,
    };

    return (
      <Stack
        direction="row"
        spacing={0.6}
        alignItems="center"
        justifyContent="center"
      >
        <Button
          variant="text"
          onClick={(event) => openFilterMenu(event, filterKey)}
          sx={{
            ...HEADER_TRIGGER_SX,
            minWidth: minWidthByFilterKey[filterKey] || HEADER_TRIGGER_SX.minWidth,
            color: active ? "#648315" : "#00336C",
            border: active ? "1.5px solid #648315" : HEADER_TRIGGER_SX.border,
            backgroundColor: "rgba(255,255,255,0.92)",
            "&:hover": { backgroundColor: "#ffffff", borderColor: active ? "#648315" : "rgba(255,255,255,0.8)" },
          }}
          endIcon={<ArrowDropDownIcon sx={{ fontSize: "1.15rem" }} />}
        >
          {label}
        </Button>
        {sortable && (
          <TableSortLabel
            active={orderBy === sortKey}
            direction={orderBy === sortKey ? orderDirection : "asc"}
            onClick={() => onSort(sortKey)}
            sx={{
              "& .MuiTableSortLabel-icon": {
                fontSize: "1rem",
              },
            }}
          />
        )}
      </Stack>
    );
  };

  const renderFilterMenu = () => {
    if (!activeFilter) return null;

    const commonMenuProps = {
      open: Boolean(filterAnchor),
      anchorEl: filterAnchor,
      onClose: closeFilterMenu,
      anchorOrigin: {
        vertical: "bottom",
        horizontal: "left",
      },
      transformOrigin: {
        vertical: "top",
        horizontal: "left",
      },
      marginThreshold: 16,
      MenuListProps: {
        dense: true,
        sx: {
          py: 0,
          maxHeight: 520,
          width: FILTER_MENU_WIDTH,
          maxWidth: FILTER_MENU_MAX_WIDTH,
          boxSizing: "border-box",
          overflowX: "hidden",
        },
      },
      ...SELECT_MENU_PROPS,
      slotProps: {
        ...SELECT_MENU_PROPS.slotProps,
      },
      PaperProps: {
        ...SELECT_MENU_PROPS.PaperProps,
        sx: {
          ...(SELECT_MENU_PROPS.PaperProps?.sx || {}),
          width: FILTER_MENU_WIDTH,
          maxWidth: FILTER_MENU_MAX_WIDTH,
          boxSizing: "border-box",
          maxHeight: 560,
          overflowX: "hidden",
          overflowY: "auto",
          py: 0,
          "& .MuiTextField-root": {
            maxWidth: "100%",
          },
        },
      },
    };

    if (activeFilter === "city") {
      return (
        <Menu {...commonMenuProps}>
          <Box sx={SEARCH_FIELD_SX}>
            <TextField
              autoFocus
              fullWidth
              size="small"
              value={filterSearch}
              onChange={(event) => setFilterSearch(event.target.value)}
              onKeyDown={(event) => event.stopPropagation()}
              placeholder="Search city or state"
              sx={FILTER_SEARCH_INPUT_SX}
              slotProps={{
                input: {
                  startAdornment: <SearchIcon sx={{ mr: 1, color: "#00336C" }} />,
                },
              }}
            />
          </Box>
          <MenuItem
            selected={!filters.city}
            onClick={() => {
              onFilterChange("city", "");
              closeFilterMenu();
            }}
          >
            All Cities
          </MenuItem>
          {filteredCityOptions.map((option) => (
            <MenuItem
              key={option.label}
              selected={normalizeCityLabel(filters.city) === option.city}
              onClick={() => {
                onFilterChange("city", option.city);
                closeFilterMenu();
              }}
            >
              {option.label}
            </MenuItem>
          ))}
        </Menu>
      );
    }

    if (activeFilter === "sector") {
      const selectedSectors = Array.isArray(filters.sector) ? filters.sector : [];

      return (
        <Menu {...commonMenuProps}>
          <Box sx={SEARCH_FIELD_SX}>
            <TextField
              autoFocus
              fullWidth
              size="small"
              value={filterSearch}
              onChange={(event) => setFilterSearch(event.target.value)}
              onKeyDown={(event) => event.stopPropagation()}
              placeholder="Search sector"
              sx={FILTER_SEARCH_INPUT_SX}
              slotProps={{
                input: {
                  startAdornment: <SearchIcon sx={{ mr: 1, color: "#00336C" }} />,
                },
              }}
            />
          </Box>
          <MenuItem
            selected={selectedSectors.length === 0}
            onClick={() => onFilterChange("sector", [])}
          >
            All Sectors
          </MenuItem>
          {filteredSectorOptions.map((option) => {
            const checked = selectedSectors.includes(option);

            return (
              <MenuItem
                key={option}
                selected={checked}
                onClick={() => {
                  onFilterChange(
                    "sector",
                    checked
                      ? selectedSectors.filter((value) => value !== option)
                      : [...selectedSectors, option],
                  );
                }}
              >
                {formatBroadSectorLabel(option)}
              </MenuItem>
            );
          })}
        </Menu>
      );
    }

    if (activeFilter === "industry") {
      const selectedIndustries = Array.isArray(filters.industry)
        ? filters.industry
        : filters.industry
          ? [filters.industry]
          : [];

      return (
        <Menu {...commonMenuProps}>
          <Box sx={SEARCH_FIELD_SX}>
            <TextField
              autoFocus
              fullWidth
              size="small"
              value={filterSearch}
              onChange={(event) => setFilterSearch(event.target.value)}
              onKeyDown={(event) => event.stopPropagation()}
              placeholder="Search NAICS"
              sx={FILTER_SEARCH_INPUT_SX}
              slotProps={{
                input: {
                  startAdornment: <SearchIcon sx={{ mr: 1, color: "#00336C" }} />,
                },
              }}
            />
          </Box>
          <MenuItem
            selected={selectedIndustries.length === 0}
            onClick={() => onFilterChange("industry", [])}
          >
            All NAICS
          </MenuItem>
          {filteredIndustryOptions.map((option) => {
            const checked = selectedIndustries.includes(option);

            return (
              <MenuItem
                key={option}
                selected={checked}
                onClick={() => {
                  onFilterChange(
                    "industry",
                    checked
                      ? selectedIndustries.filter((value) => value !== option)
                      : [...selectedIndustries, option],
                  );
                }}
              >
                {option}
              </MenuItem>
            );
          })}
        </Menu>
      );
    }

    if (activeFilter === "name") {
      return (
        <Menu {...commonMenuProps}>
          <MenuItem
            selected={orderBy === "name" && orderDirection === "asc"}
            onClick={() => { onSort("name", "asc"); closeFilterMenu(); }}
          >
            Name A → Z
          </MenuItem>
          <MenuItem
            selected={orderBy === "name" && orderDirection === "desc"}
            onClick={() => { onSort("name", "desc"); closeFilterMenu(); }}
          >
            Name Z → A
          </MenuItem>
          <Divider sx={{ my: 0.5 }} />
          <Typography
            sx={{ px: 2, py: 0.5, fontSize: "0.68rem", fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.06em" }}
          >
            Filter by Status
          </Typography>
          <MenuItem
            selected={!filters.pipeline_stage}
            onClick={() => { onFilterChange("pipeline_stage", ""); closeFilterMenu(); }}
          >
            All Statuses
          </MenuItem>
          {filteredStatusOptions.filter((o) => o !== "No Status").map((option) => (
            <MenuItem
              key={option}
              selected={filters.pipeline_stage === option}
              onClick={() => { onFilterChange("pipeline_stage", option); closeFilterMenu(); }}
            >
              {option}
            </MenuItem>
          ))}
          <MenuItem
            selected={filters.pipeline_stage === "__NONE__"}
            onClick={() => { onFilterChange("pipeline_stage", "__NONE__"); closeFilterMenu(); }}
          >
            No Status
          </MenuItem>
        </Menu>
      );
    }

    return null;
  };

  return (
    <>
      <TableContainer
        component="div"
        sx={{
          background: "#00336C",
          overflow: "hidden",
          width: "100%",
          overflowX: "auto",
        }}
      >
        <Table
          size="small"
          sx={{
            minWidth: { xs: 1140, md: 1280 },
            tableLayout: "fixed",
            borderCollapse: "separate",
            borderSpacing: 0,
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                align="center"
                padding="checkbox"
                sx={{ ...HEAD_CELL_SX, width: 48 }}
              >
                <Checkbox
                  indeterminate={someSelected && !allSelected}
                  checked={allSelected}
                  onChange={togglePage}
                  icon={<RadioButtonUncheckedIcon />}
                  checkedIcon={<CheckCircleIcon />}
                  indeterminateIcon={<RemoveCircleIcon />}
                  sx={{
                    color: "rgba(255,255,255,0.6)",
                    "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: "#ffffff" },
                  }}
                />
              </TableCell>

              {columns.map((col) => (
                <TableCell
                  key={col.id}
                  align="center"
                  sx={{
                    ...HEAD_CELL_SX,
                    ...(col.id === "name" && {
                      width: NAME_COLUMN_WIDTH,
                      maxWidth: NAME_COLUMN_WIDTH,
                    }),
                    ...(col.id === "location" && {
                      width: LOCATION_COLUMN_WIDTH,
                      maxWidth: LOCATION_COLUMN_WIDTH,
                    }),
                    ...(col.id === "sector" && {
                      width: SECTOR_COLUMN_WIDTH,
                      maxWidth: SECTOR_COLUMN_WIDTH,
                    }),
                    ...(col.id === "industry" && {
                      width: NAICS_COLUMN_WIDTH,
                      minWidth: NAICS_COLUMN_WIDTH,
                      maxWidth: NAICS_COLUMN_WIDTH,
                    }),
                    ...(col.id === "explore" && {
                      width: EXPLORE_COLUMN_WIDTH,
                      minWidth: EXPLORE_COLUMN_WIDTH,
                      maxWidth: EXPLORE_COLUMN_WIDTH,
                    }),
                  }}
                >
                  {col.id === "location"
                    ? renderFilterTrigger("Location", "city")
                    : col.id === "sector"
                      ? renderFilterTrigger("Sector", "sector", true, "sector")
                      : col.id === "industry"
                        ? renderFilterTrigger("NAICS Text", "industry", true, "industry")
                        : col.id === "name"
                          ? renderFilterTrigger(col.label, "name")
                          : col.sortable ? (
                            <TableSortLabel
                              active={orderBy === col.id}
                              direction={orderBy === col.id ? orderDirection : "asc"}
                              onClick={() => onSort(col.id)}
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#ffffff !important",
                                "&:hover": { color: "#ffffff !important" },
                                "&.Mui-active": { color: "#ffffff !important" },
                                "& .MuiTableSortLabel-icon": { color: "#ffffff !important" },
                              }}
                            >
                              {col.label}
                            </TableSortLabel>
                          ) : (
                            col.label
                          )}
                </TableCell>
              ))}

            </TableRow>
          </TableHead>

          <TableBody sx={{ background: "white" }}>
            {companies.map((company, index) => {
              const baseStriped = index % 2 === 0 ? "#fafafa" : "white";

              return (
                <TableRow
                  key={company.id ?? index}
                  sx={{
                    ...TABLE_ROW_HOVER_SX,
                    backgroundColor: baseStriped,
                    "&:hover": { backgroundColor: "#eef2f7" },
                  }}
                >
                  <TableCell align="center" padding="checkbox">
                    <Checkbox
                      checked={selectedIds.has(String(company.id))}
                      onChange={() => toggleOne(String(company.id))}
                      icon={<RadioButtonUncheckedIcon />}
                      checkedIcon={<CheckCircleIcon />}
                    />
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      ...TABLE_STYLES.bodyCell,
                      width: NAME_COLUMN_WIDTH,
                      maxWidth: NAME_COLUMN_WIDTH,
                    }}
                  >
                    <Stack direction="column" spacing={0.4} alignItems="center">
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Tooltip title={company.name || ""}>
                          <Button
                            variant="text"
                            sx={{
                              ...TABLE_STYLES.clickableCell,
                              fontWeight: "bold",
                            }}
                            onClick={() => navigate(`${detailRouteBase}/${company.id}`)}
                          >
                            <span
                              style={{
                                display: "inline-block",
                                maxWidth: NAME_COLUMN_WIDTH - 32,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {company.name}
                            </span>
                          </Button>
                        </Tooltip>

                        {company.added_to_external_db && (
                          <Tooltip title="Added to External DB">
                            <CloudDoneIcon sx={{ fontSize: 18, color: "#648315" }} />
                          </Tooltip>
                        )}
                      </Stack>
                      {(() => {
                        const cfg = STATUS_CONFIG[company.pipeline_stage] || NO_STATUS_CONFIG;
                        const label = company.pipeline_stage || "No Status";
                        return (
                          <Tooltip
                            title={STATUS_TOOLTIPS[label] || DEFAULT_STATUS_TOOLTIP}
                            arrow
                            placement="top"
                          >
                            <Button
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setStatusChangeAnchor(e.currentTarget);
                                setStatusChangeCompany(company);
                              }}
                              endIcon={<ArrowDropDownIcon sx={{ fontSize: "0.8rem !important", ml: "-4px" }} />}
                              sx={{
                                height: 18,
                                fontSize: "0.63rem",
                                fontWeight: 600,
                                textTransform: "none",
                                borderRadius: 10,
                                px: 0.75,
                                py: 0,
                                minWidth: 0,
                                lineHeight: 1.2,
                                backgroundColor: cfg.bg,
                                color: cfg.color,
                                border: `1px solid ${cfg.border}`,
                                "&:hover": {
                                  backgroundColor: cfg.bg,
                                  borderColor: cfg.color,
                                  opacity: 0.85,
                                },
                                "& .MuiButton-endIcon": { ml: 0.25 },
                              }}
                            >
                              {label}
                            </Button>
                          </Tooltip>
                        );
                      })()}
                    </Stack>
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      ...TABLE_STYLES.bodyCell,
                      width: LOCATION_COLUMN_WIDTH,
                      maxWidth: LOCATION_COLUMN_WIDTH,
                    }}
                  >
                    <Tooltip
                      title={formatBRELocationLabel({
                        city: company.city,
                        state: company.state
                          ? US_STATE_MAP[company.state.toUpperCase()] || company.state
                          : "",
                      })}
                    >
                      <span
                        style={{
                          display: "inline-block",
                          maxWidth: LOCATION_COLUMN_WIDTH,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {formatBRELocationLabel({
                          city: company.city,
                          state: company.state
                            ? US_STATE_MAP[company.state.toUpperCase()] || company.state
                            : "",
                        })}
                      </span>
                    </Tooltip>
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      ...TABLE_STYLES.bodyCell,
                      width: SECTOR_COLUMN_WIDTH,
                      maxWidth: SECTOR_COLUMN_WIDTH,
                    }}
                  >
                    <Tooltip
                      title={getSectorDisplayParts(company.sector).standardizedSector || ""}
                    >
                      <span
                        style={{
                          display: "inline-block",
                          maxWidth: SECTOR_COLUMN_WIDTH,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {getSectorDisplayParts(company.sector).standardizedSector || "-"}
                      </span>
                    </Tooltip>
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      ...TABLE_STYLES.bodyCell,
                      width: NAICS_COLUMN_WIDTH,
                      minWidth: NAICS_COLUMN_WIDTH,
                      maxWidth: NAICS_COLUMN_WIDTH,
                      pr: `${NAICS_EXPLORE_GUTTER}px`,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    <Tooltip title={getBreNaicsLabel(company)}>
                      <span
                        style={{
                          display: "inline-block",
                          maxWidth: NAICS_COLUMN_WIDTH - NAICS_EXPLORE_GUTTER,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {getBreNaicsLabel(company)}
                      </span>
                    </Tooltip>
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      ...TABLE_STYLES.bodyCell,
                      position: "sticky",
                      right: 0,
                      backgroundColor: baseStriped,
                      zIndex: 1,
                      minWidth: EXPLORE_COLUMN_WIDTH,
                      borderLeft: "1px solid #eee",
                      boxShadow: "-2px 0 6px rgba(0,0,0,0.05)",
                    }}
                  >
                    <Tooltip title="Explore company">
                      <IconButton
                        size="small"
                        onClick={(event) => handleOpenExploreMenu(event, company)}
                        sx={{ p: 0.5 }}
                      >
                        <TravelExploreIcon sx={TABLE_ICON_BLUE_SX} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>

                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        {renderFilterMenu()}

        {activeFilter && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
            <Button
              variant="text"
              onClick={clearActiveFilter}
              sx={{ textTransform: "none" }}
            >
              Clear active filter
            </Button>
          </Box>
        )}
      </TableContainer>

      <Menu
        anchorEl={statusChangeAnchor}
        open={Boolean(statusChangeAnchor)}
        onClose={() => { setStatusChangeAnchor(null); setStatusChangeCompany(null); }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        MenuListProps={{ dense: true }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 190,
              borderRadius: 2,
              border: "1px solid rgba(0,51,108,0.15)",
              boxShadow: "0 8px 24px rgba(0,51,108,0.12)",
            },
          },
        }}
      >
        <MenuItem
          selected={!statusChangeCompany?.pipeline_stage}
          onClick={() => {
            handleUpdateStage(statusChangeCompany.id, "");
            setStatusChangeAnchor(null);
            setStatusChangeCompany(null);
          }}
        >
          No Status
        </MenuItem>
        {PIPELINE_STAGE_OPTIONS.map((opt) => (
          <MenuItem
            key={opt}
            selected={statusChangeCompany?.pipeline_stage === opt}
            onClick={() => {
              handleUpdateStage(statusChangeCompany.id, opt);
              setStatusChangeAnchor(null);
              setStatusChangeCompany(null);
            }}
          >
            {opt}
          </MenuItem>
        ))}
      </Menu>

      <Menu
        anchorEl={exploreAnchorEl}
        open={isExploreMenuOpen}
        onClose={handleCloseExploreMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 210,
              borderRadius: 2,
              border: "1px solid rgba(0, 51, 108, 0.12)",
              boxShadow: "0 10px 24px rgba(0, 51, 108, 0.16)",
              mt: 0.5,
            },
          },
        }}
      >
        {exploreActions.map((action) => (
          <MenuItem
            key={action.key}
            sx={TABLE_EXPLORE_MENU_ITEM_SX}
            onClick={() => {
              handleCloseExploreMenu();
              action.onClick();
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>{action.icon}</ListItemIcon>
            <ListItemText primary={action.label} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

export default React.memo(BRECompaniesTable);
